/**
 * Get button classes based on variant and theme
 * @param {Object} options - Button options
 * @param {boolean} options.darkMode - Whether dark mode is enabled
 * @param {Object} options.theme - Theme object with primary, hover, etc.
 * @param {string} options.variant - Button variant: 'primary' | 'secondary' | 'icon'
 * @param {boolean} options.fullWidth - Whether button should be full width
 * @returns {string} - Tailwind CSS classes for the button
 */
export const getButtonClass = ({ darkMode, theme, variant = 'primary', fullWidth = false }) => {
  const baseClasses = 'px-6 py-3 rounded-xl font-medium transition-colors';
  const widthClass = fullWidth ? 'w-full' : '';

  if (variant === 'primary') {
    return `${widthClass} ${baseClasses} text-white ${theme.primary} ${theme.hover}`;
  }

  if (variant === 'secondary') {
    const borderColor = darkMode ? 'border-slate-600' : 'border-slate-300';
    const hoverBg = darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50';
    return `${widthClass} ${baseClasses} border-2 ${borderColor} ${hoverBg}`;
  }

  if (variant === 'icon') {
    const bgColor = darkMode ? 'bg-slate-700' : 'bg-slate-100';
    const hoverBg = darkMode ? 'hover:bg-slate-600' : 'hover:bg-slate-200';
    return `p-2 rounded-lg ${bgColor} ${hoverBg} transition-colors`;
  }

  return baseClasses;
};

/**
 * Get toggle button classes for segmented controls
 * @param {boolean} isActive - Whether this toggle option is active
 * @param {string} activeColor - Color for active state (e.g., 'text-sky-600')
 * @returns {string} - Tailwind CSS classes for the toggle button
 */
export const getToggleButtonClass = (isActive, activeColor = 'text-sky-600') => {
  const baseClasses = 'px-3 py-1 rounded-md text-sm font-medium transition-all';

  if (isActive) {
    return `${baseClasses} bg-white ${activeColor}`;
  }

  return `${baseClasses} text-white`;
};

/**
 * Get card container classes based on dark mode
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {string} - Tailwind CSS classes for card container
 */
export const getCardClass = (darkMode) => {
  if (darkMode) {
    return 'bg-slate-800 rounded-2xl shadow-lg border border-slate-700';
  }
  return 'bg-white rounded-2xl shadow-lg border border-slate-200';
};

/**
 * Get text color classes for secondary text
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {string} - Tailwind CSS classes for secondary text
 */
export const getSecondaryTextClass = (darkMode) => {
  return darkMode ? 'text-slate-400' : 'text-slate-600';
};

/**
 * Get text color classes for muted/tertiary text
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {string} - Tailwind CSS classes for muted text
 */
export const getMutedTextClass = (darkMode) => {
  return darkMode ? 'text-slate-500' : 'text-slate-400';
};

/**
 * Get border color classes
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {string} - Tailwind CSS classes for borders
 */
export const getBorderClass = (darkMode) => {
  return darkMode ? 'border-slate-700' : 'border-slate-200';
};

/**
 * Get background color based on theme and dark mode
 * @param {string} themePrimary - Theme primary color class (e.g., 'bg-sky-500')
 * @param {boolean} darkMode - Whether dark mode is enabled
 * @returns {string} - Hex color code for background
 */
export const getThemeBackgroundColor = (themePrimary, darkMode = false) => {
  const lightThemeColors = {
    'bg-sky-500': '#e0f2fe',       // sky-100 (blue theme)
    'bg-emerald-500': '#d1fae5',   // emerald-100 (meadow theme)
    'bg-amber-600': '#fef3c7',     // amber-100 (amber theme)
    'bg-emerald-700': '#d1fae5',   // emerald-100 (forest theme)
    'bg-yellow-700': '#fef9c3',    // yellow-100 (hazelnut theme)
    'bg-orange-500': '#ffedd5',    // orange-100 (sunset theme)
    'bg-teal-600': '#ccfbf1',      // teal-100 (ocean theme)
  };

  const darkThemeColors = {
    'bg-sky-500': '#020617',       // slate-950 with blue tint (blue theme)
    'bg-emerald-500': '#021208',   // very dark green (meadow theme)
    'bg-amber-600': '#0f0a05',     // very dark amber (amber theme)
    'bg-emerald-700': '#020f08',   // very dark forest green (forest theme)
    'bg-yellow-700': '#0f0c02',    // very dark yellow/brown (hazelnut theme)
    'bg-orange-500': '#0f0502',    // very dark orange (sunset theme)
    'bg-teal-600': '#020f0d',      // very dark teal (ocean theme)
  };

  if (darkMode) {
    return darkThemeColors[themePrimary] || '#020617'; // default slate-950
  }

  return lightThemeColors[themePrimary] || '#f1f5f9'; // default slate-100
};
